
import CommonCss from "lib/common/style.module.scss";

import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { getSelectedBusinessData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import Badge from "nlib/ui/Badge";
import ConfidencePercents from "nlib/common/ConfidencePercents";
import DataStatuses from "const/DataStatuses";
import DateTimeFormats from "const/DateTimeFormats";
import DocumentPaymentTypes from "const/DocumentPaymentTypes";
import DocumentRecogniseStatuses from "const/DocumentRecogniseStatuses";
import DocumentTypes from "const/DocumentTypes";
import DocumentsStatuses from "nlib/pages/DocumentsPage/DocumentsStatuses";
import Money from "utils/Money";
import React, { useCallback } from "react";
import classNames from "classnames";
import moment from "moment";

const { DATE_TEXT } = DateTimeFormats;

const StatusContent = ({ status, recogniseData, duplicatedDocumentsIds }) => {
  const { uiTexts } = useSelector(getTextsData);

  const toReviewStatus = status === DataStatuses.TO_REVIEW;

  const errorState = status === DataStatuses.TO_EXTRACT && recogniseData.status === DocumentRecogniseStatuses.ERROR;

  const duplicatedDocument = Array.isArray(duplicatedDocumentsIds) && duplicatedDocumentsIds.length;

  if (errorState) {
    return (
      <Badge negative className={Css.status} value={uiTexts.error} />
    );
  }
  if (duplicatedDocument) {
    return (
      <Badge warning className={Css.status} value={uiTexts.duplicate} />
    );
  }
  if (toReviewStatus && !recogniseData.confidence) {
    return (
      <Badge primary className={Css.status} value={uiTexts.edited} />
    );
  }
  if (recogniseData.confidence) {
    return (
      <ConfidencePercents
        className={`${Css.status} ${Css.percents}`}
        value={recogniseData.averageConfidence}
        component={Badge} />
    );
  }

  return null;
};

const ListItem = ({ disabled, data = {}, onItemClick }) => {
  const {
    type,
    status,
    currency,
    vendorId,
    issueDate,
    amountVat,
    amountBase,
    paymentType,
    recogniseData,
    originalDocumentId,
    duplicatedDocumentsIds,
    id: documentId,
    address: { name: vendorName } = {}
  } = data;

  const { uiTexts } = useSelector(getTextsData);

  const { settings: { allowClientPayeeSelection } = {} } = useSelector(getSelectedBusinessData);

  const extractionStatus = status === DataStatuses.TO_EXTRACT;

  const exportedStatus = status === DataStatuses.EXPORTED;

  const hasDuplicates = !!(Array.isArray(duplicatedDocumentsIds) && duplicatedDocumentsIds.length);

  const amount = (+amountBase || 0) + (+amountVat || 0);

  const buyPayment = paymentType === DocumentPaymentTypes.BUY;

  const billType = paymentType === DocumentPaymentTypes.BUY && type === DocumentTypes.INVOICE;

  const typeContent = (paymentType && type && (billType ? DocumentTypes.BILL : type));

  const vendorContent = paymentType && vendorName;

  const { labelLangId: statusTitleLangId } = DocumentsStatuses.getStatusData(status) || {};

  const { status: recogniseStatus } = recogniseData || {};

  const handleListItemClick = useCallback(() => {
    onItemClick(documentId);
  }, [documentId, onItemClick]);

  return (
    <div disabled={disabled} className={Css.listItem} onClick={handleListItemClick}>
      <div className={Css.header}>
        <div className={Css.info}>
          {extractionStatus
            ? (
              <div>
                <Icons.Cloud className={Css.warning} />
                <span className={Css.neutral}>{uiTexts.processing}...</span>
              </div>
            )
            : <>
              {hasDuplicates
                ? <Icons.Copy className={Css.warning} />
                : <Icons.FileText className={recogniseStatus === DocumentRecogniseStatuses.ERROR ? Css.negative : ""} />}
              <span>
                {uiTexts[typeContent] || uiTexts.document}
                {originalDocumentId ? `: ${originalDocumentId}` : ""}
              </span>
            </>}
        </div>
        <div className={Css.amount}>
          {status !== DataStatuses.TO_EXTRACT && (
            <div
              className={(paymentType && (amount || amount === 0))
                ? CommonCss.coloredNumber
                : (paymentType ? "" : CommonCss.mediumDarkText)}
              data-negative={buyPayment ? "" : undefined}>
              {Money.toMoneyString(amount, currency)}
            </div>
          )}
        </div>
      </div>
      <div className={Css.content}>
        <div className={classNames(Css.timestamp, !issueDate && CommonCss.neutralText)}>
          {issueDate ? moment.utc(issueDate).format(DATE_TEXT) : uiTexts.noDate}
        </div>
        {!!statusTitleLangId && (
          <div className={Css.documentStatus}>
            <StatusContent
              status={status}
              recogniseData={recogniseData}
              duplicatedDocumentsIds={duplicatedDocumentsIds} />
          </div>
        )}
        <div className={Css.vendor}>
          {vendorContent
            ? ((exportedStatus || !allowClientPayeeSelection) ? <Icons.User /> : (
              <>
                {vendorId
                  ? <Icons.User className={CommonCss.positiveText} />
                  : <Icons.UserPlus className={CommonCss.highlightText} />}
              </>
            ))
            : null}
          <span className={vendorContent ? undefined : CommonCss.neutralText}>{vendorContent || uiTexts.noPayee}</span>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ListItem);
